import React, { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../common/SEO';
import useDirectusData from '../../hooks/useDirectusData';
import { fetchServices, getImageUrl } from '../../api/directus';

/**
 * ServicesPage component
 * Displays detailed information about the services offered
 * Fetches data from Directus CMS
 */
const ServicesPage = () => {
  // Fetch services data from Directus CMS
  const { data, loading, error } = useDirectusData(fetchServices);
  // Scroll to anchor on load if present in URL
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Remove the # symbol
      const id = hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        // Use setTimeout to ensure the page has fully loaded
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, []);

  // Parse SVG icons from CMS or use fallback icons
  const getIconComponent = (iconString) => {
    if (iconString && iconString.startsWith('<svg')) {
      return (
        <div 
          className="w-16 h-16 flex items-center justify-center overflow-hidden" 
          dangerouslySetInnerHTML={{ 
            __html: iconString.replace('<svg', '<svg class="w-full h-full"') 
          }} 
        />
      );
    }
    
    // Fallback icons
    return (
      <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M8.5 12.5L10.5 16.5L15.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  };

  // Create a services array from the CMS data or use fallback if loading/error
  const services = data?.data ? 
    data.data.map(service => ({
      id: service.title.toLowerCase().replace(/\s+/g, '-').replace(/[&]/g, 'and'),
      icon: getIconComponent(service.icon),
      title: service.title,
      description: service.description,
      details: service.details || [],
      benefits: service.benefits
    })) : 
    [
    {
      id: "game-translation",
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
          <path d="M17.5 9C19.9853 9 22 11.0147 22 13.5C22 15.9853 19.9853 18 17.5 18H6.5C4.01472 18 2 15.9853 2 13.5C2 11.0147 4.01472 9 6.5 9H17.5Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M6.5 12V15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M5 13.5H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M17 12H17.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M16 14H16.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M18 14H18.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M17 16H17.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M12 13.5H12.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      title: "Game Translation & Localization",
      description: "Full in-game text translation for a seamless gaming experience.",
      details: [
        "Complete translation of all in-game text, including UI, dialogues, and tutorials",
        "Cultural adaptation to ensure content resonates with French-speaking players",
        "Consistent terminology throughout the game",
        "Preservation of the game's tone and atmosphere",
        "Testing to ensure translations fit within UI constraints"
      ],
      benefits: "Our game translation services ensure French players can fully immerse themselves in your game world. We pay special attention to maintaining your game's unique voice and style while making it culturally relevant to French-speaking audiences."
    },
    {
      id: "website-localization",
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M12 2C14.6522 2 17.1957 3.05357 19.0711 4.92893C20.9464 6.8043 22 9.34784 22 12C22 14.6522 20.9464 17.1957 19.0711 19.0711C17.1957 20.9464 14.6522 22 12 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M2 12H22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M12 2V22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M4.92896 19.0711C6.8043 20.9465 9.34784 22 12 22C14.6522 22 17.1957 20.9465 19.0711 19.0711" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M19.0711 4.92896C17.1957 3.05357 14.6522 2 12 2C9.34784 2 6.8043 3.05357 4.92896 4.92896" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      title: "Website & Marketing Localization",
      description: "Ensure your store and marketing materials appeal to French-speaking audiences.",
      details: [
        "Translation of your game's website and landing pages",
        "Localization of marketing materials, press releases, and promotional content",
        "Social media content translation",
        "Email campaign localization",
        "Culturally-adapted copywriting that converts"
      ],
      benefits: "Effective marketing requires more than just translation—it needs cultural adaptation. We help you craft marketing messages that resonate with French-speaking players, increasing your game's appeal and driving more sales in French-speaking markets."
    },
    {
      id: "steam-page",
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M16.5 8.5C16.5 9.88071 15.3807 11 14 11C12.6193 11 11.5 9.88071 11.5 8.5C11.5 7.11929 12.6193 6 14 6C15.3807 6 16.5 7.11929 16.5 8.5Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M7.5 15.5C7.5 14.1193 8.61929 13 10 13C11.3807 13 12.5 14.1193 12.5 15.5C12.5 16.8807 11.3807 18 10 18C8.61929 18 7.5 16.8807 7.5 15.5Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M16 11L10 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      title: "Steam Page Localization",
      description: "Optimize your Steam store page for French-speaking players to boost visibility and sales.",
      details: [
        "Translation of game descriptions, features, and system requirements",
        "Localization of store graphics with French text",
        "Keyword optimization for French search terms",
        "Translation of updates and patch notes",
        "Culturally-adapted screenshots and captions"
      ],
      benefits: "A professionally localized Steam page significantly increases your visibility to French-speaking players. We optimize your store page not just for linguistic accuracy but also for discoverability and conversion in the French market."
    },
    {
      id: "ongoing-support",
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
          <path d="M19.7285 10.9288C20.4413 13.5978 19.7507 16.5635 17.6569 18.6573C14.5327 21.7815 9.46734 21.7815 6.34315 18.6573C3.21895 15.5331 3.21895 10.4677 6.34315 7.34354C9.46734 4.21935 14.5327 4.21935 17.6569 7.34354L18.364 8.05065" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M14.1213 8.05029L19.0355 8.05029L19.0355 3.13608" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: "Ongoing Support & Updates",
      description: "Continuous translation updates as new content is released for your game.",
      details: [
        "Quick turnaround for new content updates",
        "Consistent terminology with previous translations",
        "DLC and expansion pack localization",
        "Patch notes translation",
        "Community feedback integration for translation improvements"
      ],
      benefits: "Games evolve over time, and so should their translations. Our ongoing support ensures your French localization stays current with every update, maintaining a seamless experience for French players throughout your game's lifecycle."
    },
    {
      id: "documentation",
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
          <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M7 7H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M7 12H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M7 17H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      title: "Game Documentation",
      description: "Translation of game manuals, guides, and technical documentation.",
      details: [
        "User manual translation",
        "Strategy guide localization",
        "Wiki content translation",
        "Technical documentation for modders",
        "In-game help systems"
      ],
      benefits: "Comprehensive documentation translation ensures French players have access to all the information they need to fully enjoy your game. From basic controls to advanced strategies, we make sure nothing gets lost in translation."
    },
    {
      id: "community-management",
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-16">
          <path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M5.63605 5.63605L8.46447 8.46447" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M15.5355 15.5355L18.364 18.364" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M15.5355 8.46447L18.364 5.63605" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M5.63605 18.364L8.46447 15.5355" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      title: "French Community Management",
      description: "Engage with your French-speaking player base through native communication.",
      details: [
        "Translation of community announcements",
        "Moderation of French community forums",
        "Social media engagement in French",
        "Player feedback collection and translation",
        "French customer support assistance"
      ],
      benefits: "Building a strong community requires speaking your players' language. Our community management services help you engage directly with your French-speaking players, building loyalty and gathering valuable feedback to improve your game."
    }
  ];

  // Animation variants
  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  // Structured data for the services page
  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Professional French Game Localization Services",
    "description": "Professional French game localization services including in-game text translation, marketing materials, Steam page optimization, and ongoing support.",
    "url": "https://natuxtranslations.com/services",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": services.map((service, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": service.title,
        "description": service.description,
        "url": `https://natuxtranslations.com/services#${service.id}`
      }))
    }
  };

  return (
    <>
      <SEO 
        title="Services"
        description="Professional French game localization services including in-game text translation, marketing materials, Steam page optimization, and ongoing support."
        schema={servicesSchema}
      />
      
      {/* Hero Section */}
      <section className="py-32 bg-bg-alt relative">
        <div className="container mx-auto px-6">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={titleVariants}
          >
            <div className="w-16 h-px bg-primary mx-auto mb-12"></div>
            <h1 className="font-heading text-4xl md:text-6xl font-normal mb-8 tracking-wide">
              Our Services
            </h1>
            <p className="text-light text-lg md:text-xl font-light leading-relaxed">
              We provide comprehensive French localization services tailored to game developers' needs.
              From in-game text to marketing materials, we ensure your game resonates with French-speaking players.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Services List */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="space-y-32">
            {services.map((service, index) => (
              <ServiceItem 
                key={index}
                service={service}
                isEven={index % 2 === 0}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* Process Overview */}
      <section className="py-24 bg-bg-alt">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-20">
            <div className="w-16 h-px bg-primary mx-auto mb-12"></div>
            <h2 className="font-heading text-3xl md:text-4xl font-normal mb-8 tracking-wide">
              Our Translation Process
            </h2>
            <p className="text-light text-lg font-light leading-relaxed">
              We follow a structured approach to ensure high-quality, consistent translations for your game.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="border border-primary/10 p-10 hover:border-primary transition-all duration-500 text-center">
              <div className="w-16 h-16 bg-primary/5 rounded-full flex items-center justify-center mx-auto mb-8">
                <span className="text-primary text-2xl font-heading">01</span>
              </div>
              <h3 className="text-xl font-heading mb-6 tracking-wide">Analysis & Planning</h3>
              <p className="text-light font-light">
                We analyze your game's content, style, and target audience to create a tailored translation strategy.
              </p>
            </div>
            
            <div className="border border-primary/10 p-10 hover:border-primary transition-all duration-500 text-center">
              <div className="w-16 h-16 bg-primary/5 rounded-full flex items-center justify-center mx-auto mb-8">
                <span className="text-primary text-2xl font-heading">02</span>
              </div>
              <h3 className="text-xl font-heading mb-6 tracking-wide">Translation & Adaptation</h3>
              <p className="text-light font-light">
                Our native French translators work on your content, ensuring it maintains your game's unique voice.
              </p>
            </div>
            
            <div className="border border-primary/10 p-10 hover:border-primary transition-all duration-500 text-center">
              <div className="w-16 h-16 bg-primary/5 rounded-full flex items-center justify-center mx-auto mb-8">
                <span className="text-primary text-2xl font-heading">03</span>
              </div>
              <h3 className="text-xl font-heading mb-6 tracking-wide">Review & Implementation</h3>
              <p className="text-light font-light">
                We review translations for quality and consistency, then help implement them in your game.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-24 bg-primary text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-normal mb-8 tracking-wide">
            Ready to Expand Your Reach?
          </h2>
          <p className="text-xl text-white/80 font-light mb-12 max-w-2xl mx-auto">
            Contact us today to discuss your French localization needs and get a personalized quote.
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-white text-primary px-10 py-4 uppercase tracking-widest text-sm font-light transition-all duration-300 hover:bg-white/90 hover:translate-y-[-2px]"
          >
            Get a Quote
          </Link>
        </div>
      </section>
    </>
  );
};

/**
 * ServiceItem component
 * Displays a single service with details
 */
const ServiceItem = ({ service, isEven }) => {
  const itemRef = useRef(null);
  const isInView = useInView(itemRef, { once: true, amount: 0.2 });
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };

  return (
    <motion.div 
      id={service.id}
      ref={itemRef}
      className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 lg:gap-20 items-center`}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      {/* Icon */}
      <div className="w-full lg:w-1/3 flex justify-center">
        <div className="w-40 h-40 bg-primary/5 rounded-full flex items-center justify-center text-primary">
          {service.icon}
        </div>
      </div>
      
      {/* Content */}
      <div className="w-full lg:w-2/3">
        <h2 className="font-heading text-3xl mb-6 tracking-wide">{service.title}</h2>
        <p className="text-light text-lg font-light mb-8 leading-relaxed">
          {service.description}
        </p>
        
        <h3 className="text-xl font-heading mb-4">What's Included:</h3>
        <ul className="list-disc pl-6 mb-8 space-y-2 text-light">
          {service.details.map((detail, index) => (
            <li key={index} className="font-light">{detail}</li>
          ))}
        </ul>
        
        <div className="bg-primary/5 p-6 border-l-4 border-primary">
          <h3 className="text-xl font-heading mb-3">Benefits</h3>
          <p className="text-light font-light">{service.benefits}</p>
        </div>
      </div>
    </motion.div>
  );
};

export default ServicesPage;
